import React, { useState, useEffect } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, Search, Trophy, Flame, Award, Calendar, 
  UserCheck, UserX, Edit, X, Check, ChevronLeft,
  History, TrendingUp, Cake, Trash2, UserPlus, Eye, EyeOff, RefreshCw, Loader2,
  Ban, ShieldOff, MailX, Mail
} from 'lucide-react';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get('filter') || 'all');
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', is_active: true, birthdate: '', subscription_start_date: '' });
  const [saving, setSaving] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [adding, setAdding] = useState(false);
  const [recalculatingAll, setRecalculatingAll] = useState(false);
  const [fixingStreaks, setFixingStreaks] = useState(false);
  const [badgeModalUser, setBadgeModalUser] = useState(null); // For showing user's badges
  const [unsubInsights, setUnsubInsights] = useState(null); // Aggregated "why did you leave" reasons
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, [filter]);

  useEffect(() => {
    // Load unsubscribe feedback insights (silent — non-blocking)
    (async () => {
      try {
        const res = await apiClient.get('/admin/unsubscribe-feedback?days=90');
        setUnsubInsights(res.data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let endpoint = '/admin/users';
      if (filter === 'active') {
        endpoint += '?status=active';
      } else if (filter === 'inactive') {
        endpoint += '?status=inactive';
      } else if (filter === 'suspended') {
        endpoint += '?status=suspended';
      } else if (filter === 'active-today') {
        const res = await apiClient.get('/admin/active-today');
        // Sort by first name alphabetically
        const sortedUsers = res.data
          .map(item => ({ ...item.user, today_points: item.today_points }))
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
        setUsers(sortedUsers);
        setLoading(false);
        return;
      }
      const res = await apiClient.get(endpoint);
      // Sort by first name alphabetically (case-insensitive)
      const sortedUsers = res.data
        .filter(u => !u.is_admin)
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
      setUsers(sortedUsers);
    } catch (err) {
      console.error('Failed to fetch users');
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      name: user.name,
      email: user.email,
      is_active: user.is_active !== false,
      birthdate: user.birthdate || '',
      exclude_from_leaderboard: user.exclude_from_leaderboard || false,
      show_as_coach_on_leaderboard: user.show_as_coach_on_leaderboard || false,
      subscription_start_date: user.subscription_start_date || ''
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/admin/users/${editingUser.id}`, editForm);
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingUser) return;
    setDeleting(true);
    try {
      await apiClient.delete(`/admin/users/${deletingUser.id}`);
      toast.success(`User ${deletingUser.email} deleted successfully`);
      setDeletingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddUser = async () => {
    if (!addForm.name || !addForm.email || !addForm.password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (addForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setAdding(true);
    try {
      await apiClient.post('/admin/users/create', addForm);
      toast.success(`User ${addForm.email} created successfully! They will receive a welcome email.`);
      setShowAddModal(false);
      setAddForm({ name: '', email: '', password: '' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setAdding(false);
    }
  };

  const toggleUserStatus = async (user) => {
    const newStatus = user.is_active === false ? true : false;
    try {
      await apiClient.put(`/admin/users/${user.id}`, { is_active: newStatus });
      toast.success(newStatus ? 'User activated' : 'User deactivated');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user status');
    }
  };

  const toggleSuspension = async (user) => {
    try {
      const res = await apiClient.post(`/admin/users/${user.id}/suspend`);
      toast.success(res.data.message);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle suspension');
    }
  };

  const toggleEmailOptOut = async (user) => {
    const nextValue = !user.email_opted_out;
    const confirmMsg = nextValue
      ? `Turn OFF marketing emails for ${user.name}? They'll still get in-app notifications and transactional emails.`
      : `Turn ON marketing emails for ${user.name}? They'll start receiving broadcasts and automation nudges again.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      await apiClient.patch(`/admin/users/${user.id}/email-optout`, { opted_out: nextValue });
      toast.success(nextValue ? `${user.name} unsubscribed from emails` : `${user.name} re-subscribed to emails`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update email preference');
    }
  };

  const recalculateAllBadges = async () => {
    if (!window.confirm('This will recalculate badges for ALL users based on their actual check-in history. This runs in the background and may take a few minutes. Continue?')) {
      return;
    }
    
    setRecalculatingAll(true);
    try {
      const res = await apiClient.post('/admin/recalculate-all-badges');
      toast.success(res.data.message, { duration: 5000 });
      toast.info(res.data.note, { duration: 7000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start badge recalculation');
    } finally {
      setRecalculatingAll(false);
    }
  };

  const fixMonthlyStreaks = async () => {
    if (!window.confirm('This will recalculate ALL users\' streaks to be monthly (not all-time). Streaks will only count consecutive days within the current month. Continue?')) {
      return;
    }
    
    setFixingStreaks(true);
    try {
      const res = await apiClient.post('/admin/recalculate-all-monthly-streaks');
      toast.success(res.data.message, { duration: 5000 });
      fetchUsers(); // Refresh the list
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fix monthly streaks');
    } finally {
      setFixingStreaks(false);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(search.toLowerCase())
      || u.email?.toLowerCase().includes(search.toLowerCase());
    if (!matchesSearch) return false;
    if (filter === 'emails-off') return u.email_opted_out === true;
    return true;
  });

  const activeCount = users.filter(u => u.is_active !== false && !u.is_suspended).length;
  const inactiveCount = users.filter(u => u.is_active === false).length;
  const suspendedCount = users.filter(u => u.is_suspended).length;
  const emailsOffCount = users.filter(u => u.email_opted_out === true).length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-stone-900 flex items-center gap-3">
              <Users className="text-[#EFB342]" />
              USERS
            </h1>
            <p className="text-stone-600 text-sm mt-1">
              {filter === 'active-today' 
                ? `${users.length} users active today`
                : `${users.length} total • ${activeCount} active • ${suspendedCount} suspended • ${inactiveCount} inactive • ${emailsOffCount} emails off`
              }
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={fixMonthlyStreaks}
              disabled={fixingStreaks}
              variant="outline"
              className="border-orange-500 text-orange-400 hover:bg-orange-500/20"
              data-testid="fix-monthly-streaks-btn"
            >
              {fixingStreaks ? (
                <><Loader2 size={18} className="mr-2 animate-spin" /> Fixing...</>
              ) : (
                <><RefreshCw size={18} className="mr-2" /> Fix Monthly Streaks</>
              )}
            </Button>
            <Button
              onClick={recalculateAllBadges}
              disabled={recalculatingAll}
              variant="outline"
              className="border-purple-500 text-purple-400 hover:bg-purple-500/20"
              data-testid="recalculate-all-badges-btn"
            >
              {recalculatingAll ? (
                <><Loader2 size={18} className="mr-2 animate-spin" /> Processing...</>
              ) : (
                <><RefreshCw size={18} className="mr-2" /> Recalculate All Badges</>
              )}
            </Button>
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#EFB342] text-black hover:bg-[#D99F30] font-semibold"
              data-testid="add-user-btn"
            >
              <UserPlus size={18} className="mr-2" />
              Add User
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              filter === 'all'
                ? 'bg-[#EFB342] text-black'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            All Users
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              filter === 'active'
                ? 'bg-green-500 text-stone-900'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            <UserCheck size={16} />
            Active
          </button>
          <button
            onClick={() => setFilter('suspended')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              filter === 'suspended'
                ? 'bg-orange-500 text-stone-900'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            data-testid="suspended-filter-btn"
          >
            <Ban size={16} />
            Suspended
          </button>
          <button
            onClick={() => setFilter('inactive')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              filter === 'inactive'
                ? 'bg-red-500 text-stone-900'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            <UserX size={16} />
            Inactive
          </button>
          <button
            onClick={() => setFilter('active-today')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              filter === 'active-today'
                ? 'bg-purple-500 text-stone-900'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            <TrendingUp size={16} />
            Active Today
          </button>
          <button
            onClick={() => setFilter('emails-off')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              filter === 'emails-off'
                ? 'bg-stone-400 text-stone-900'
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            data-testid="emails-off-filter-btn"
          >
            <MailX size={16} />
            Emails Off
          </button>
        </div>

        {/* Unsubscribe insights (only when viewing opted-out users) */}
        {filter === 'emails-off' && unsubInsights && unsubInsights.total > 0 && (
          <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-6" data-testid="unsub-insights-card">
            <div className="flex items-baseline justify-between mb-4">
              <h2 className="text-stone-900 font-semibold flex items-center gap-2">
                <MailX size={18} className="text-[#EFB342]" />
                Why they left ({unsubInsights.days}d)
              </h2>
              <span className="text-stone-500 text-xs">{unsubInsights.total} responses</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              {[
                { key: 'too_frequent', label: 'Too many emails', emoji: '📬' },
                { key: 'not_relevant', label: 'Not relevant', emoji: '🤷' },
                { key: 'finished_goals', label: 'Finished goals', emoji: '🏆' },
                { key: 'other', label: 'Other', emoji: '💭' },
              ].map((r) => (
                <div key={r.key} className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-center">
                  <div className="text-2xl mb-1">{r.emoji}</div>
                  <div className="text-2xl font-bold text-[#EFB342]">{unsubInsights.counts[r.key] || 0}</div>
                  <div className="text-xs text-stone-500 mt-0.5">{r.label}</div>
                </div>
              ))}
            </div>
            {/* Recent free-text comments (from "Other" mostly) */}
            {unsubInsights.recent?.some(r => r.comment) && (
              <div className="mt-4 pt-4 border-t border-stone-200">
                <p className="text-stone-600 text-xs mb-2">Recent comments:</p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {unsubInsights.recent.filter(r => r.comment).slice(0, 5).map((r) => (
                    <div key={r.id} className="bg-stone-50 border border-stone-200 rounded p-2 text-xs">
                      <p className="text-stone-700 italic">&ldquo;{r.comment}&rdquo;</p>
                      <p className="text-stone-400 mt-1">— {r.user_name} · {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" size={20} />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="bg-white border-stone-200 pl-10 h-12"
            data-testid="user-search-input"
          />
        </div>

        {/* Users Table */}
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200">
                  <th className="text-left p-4 text-xs text-stone-500 uppercase tracking-wider font-semibold">User</th>
                  <th className="text-center p-4 text-xs text-stone-500 uppercase tracking-wider font-semibold">Status</th>
                  <th className="text-center p-4 text-xs text-stone-500 uppercase tracking-wider font-semibold">Points</th>
                  <th className="text-center p-4 text-xs text-stone-500 uppercase tracking-wider font-semibold">Streak</th>
                  <th className="text-center p-4 text-xs text-stone-500 uppercase tracking-wider font-semibold">Badges (This Month)</th>
                  <th className="text-center p-4 text-xs text-stone-500 uppercase tracking-wider font-semibold">Last Active</th>
                  <th className="text-center p-4 text-xs text-stone-500 uppercase tracking-wider font-semibold">Membership Expires</th>
                  <th className="text-center p-4 text-xs text-stone-500 uppercase tracking-wider font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    // Calculate badges earned this month
                    const now = new Date();
                    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
                    const badgesThisMonth = (user.badges || []).filter(b => (b.earned_at || '') >= currentMonthStart);
                    
                    return (
                      <tr 
                        key={user.id}
                        className="hover:bg-[#F0EBE1] transition-colors"
                        data-testid={`user-row-${user.id}`}
                      >
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              user.is_active === false ? 'bg-red-500/20' : 'bg-stone-100'
                            }`}>
                              <Users className={user.is_active === false ? 'text-red-500' : 'text-stone-500'} size={18} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-stone-900">{user.name}</p>
                                {user.exclude_from_leaderboard && (
                                  <span className="px-1.5 py-0.5 rounded text-[10px] bg-stone-200 text-stone-600" title="Hidden from leaderboard">
                                    Hidden
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-stone-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex flex-col items-center gap-1">
                            {user.is_suspended ? (
                              <span className="px-2 py-1 rounded-full text-xs bg-orange-500/20 text-orange-400">Suspended</span>
                            ) : user.is_active === false ? (
                              <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">Inactive</span>
                            ) : (
                              <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">Active</span>
                            )}
                            {user.email_opted_out && (
                              <span
                                className="px-2 py-0.5 rounded-full text-[10px] bg-stone-100 text-stone-600 flex items-center gap-1"
                                title="This user has unsubscribed from marketing emails"
                                data-testid={`emails-off-badge-${user.id}`}
                              >
                                <MailX size={10} /> Emails off
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Trophy size={16} className="text-[#EFB342]" />
                            <span className="font-bold text-stone-900">{user.total_points || 0}</span>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Flame size={16} className="text-orange-500" />
                            <span className="font-medium text-stone-900">{user.current_streak || 0}</span>
                          </div>
                        </td>
                        <td className="p-4 text-center">
                          <button
                            onClick={() => user.badges?.length > 0 && setBadgeModalUser(user)}
                            className={`flex items-center justify-center gap-1 mx-auto px-2 py-1 rounded-lg transition-all ${
                              user.badges?.length > 0 
                                ? 'hover:bg-stone-200 cursor-pointer' 
                                : 'cursor-default'
                            }`}
                            disabled={!user.badges?.length}
                            data-testid={`badge-count-${user.id}`}
                          >
                            <Award size={16} className={badgesThisMonth.length > 0 ? 'text-[#EFB342]' : 'text-stone-400'} />
                            <span className={`font-medium ${badgesThisMonth.length > 0 ? 'text-[#EFB342]' : 'text-stone-500'}`}>
                              {badgesThisMonth.length}
                            </span>
                            {user.badges?.length > 0 && (
                              <span className="text-xs text-stone-500">/ {user.badges.length}</span>
                            )}
                          </button>
                        </td>
                        <td className="p-4 text-center">
                          <div className="flex items-center justify-center gap-1 text-stone-600">
                            <Calendar size={14} />
                            <span className="text-sm">
                              {user.last_check_in 
                                ? new Date(user.last_check_in).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : 'Never'
                              }
                            </span>
                          </div>
                        </td>
                        <td className="p-4 text-center" data-testid={`membership-expires-${user.id}`}>
                          {(() => {
                            // Membership renews annually — expiration is one year
                            // after the subscription_start_date the admin recorded.
                            const startStr = user.subscription_start_date;
                            if (!startStr) {
                              return <span className="text-xs text-stone-400">—</span>;
                            }
                            const start = new Date(startStr + 'T12:00:00');
                            const expires = new Date(start);
                            expires.setFullYear(expires.getFullYear() + 1);
                            const today = new Date();
                            const daysLeft = Math.ceil((expires - today) / (1000 * 60 * 60 * 24));
                            const dateLabel = expires.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                            const colorClass =
                              daysLeft < 0 ? 'text-red-600'
                              : daysLeft <= 14 ? 'text-amber-700'
                              : 'text-stone-600';
                            const tail =
                              daysLeft < 0 ? ` (${Math.abs(daysLeft)}d ago)`
                              : daysLeft <= 30 ? ` (${daysLeft}d)`
                              : '';
                            return (
                              <span className={`text-sm ${colorClass}`} title={`Started ${start.toLocaleDateString()}`}>
                                {dateLabel}{tail}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEdit(user)}
                            className="text-stone-600 hover:text-stone-900 hover:bg-stone-200"
                            title="Edit user"
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleSuspension(user)}
                            className={user.is_suspended 
                              ? 'text-green-400 hover:text-green-300 hover:bg-green-500/10'
                              : 'text-orange-400 hover:text-orange-300 hover:bg-orange-500/10'
                            }
                            title={user.is_suspended ? 'Reactivate subscription' : 'Suspend subscription'}
                            data-testid={`suspend-btn-${user.id}`}
                          >
                            {user.is_suspended ? <ShieldOff size={16} /> : <Ban size={16} />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleEmailOptOut(user)}
                            className={user.email_opted_out
                              ? 'text-stone-500 hover:text-[#EFB342] hover:bg-[#EFB342]/10'
                              : 'text-stone-600 hover:text-stone-800 hover:bg-stone-200'
                            }
                            title={user.email_opted_out ? 'Re-subscribe to emails' : 'Unsubscribe from emails'}
                            data-testid={`email-optout-btn-${user.id}`}
                          >
                            {user.email_opted_out ? <MailX size={16} /> : <Mail size={16} />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                            className="text-stone-600 hover:text-[#EFB342] hover:bg-[#EFB342]/10"
                            title="View history"
                          >
                            <History size={16} />
                          </Button>
                          {!user.is_admin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeletingUser(user)}
                              className="text-stone-600 hover:text-red-400 hover:bg-red-500/10"
                              title="Delete user"
                            >
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-stone-500">
                      {search ? 'No users match your search' : 'No users found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-[#F5F4F1]/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white border border-stone-200 rounded-2xl p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold text-stone-900">Edit User</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingUser(null)}
                  className="text-stone-600 hover:text-stone-900"
                >
                  <X size={20} />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-stone-700">Name</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    className="bg-stone-50 border-stone-300 text-stone-900"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-stone-700">Email</Label>
                  <Input
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    className="bg-stone-50 border-stone-300 text-stone-900"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-stone-700">Account Status</Label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setEditForm({ ...editForm, is_active: true })}
                      className={`flex-1 p-3 rounded-xl border flex items-center justify-center gap-2 transition-all ${
                        editForm.is_active
                          ? 'bg-green-500/20 border-green-500 text-green-400'
                          : 'bg-stone-100 border-stone-300 text-stone-600'
                      }`}
                    >
                      <UserCheck size={18} />
                      Active
                    </button>
                    <button
                      onClick={() => setEditForm({ ...editForm, is_active: false })}
                      className={`flex-1 p-3 rounded-xl border flex items-center justify-center gap-2 transition-all ${
                        !editForm.is_active
                          ? 'bg-red-500/20 border-red-500 text-red-400'
                          : 'bg-stone-100 border-stone-300 text-stone-600'
                      }`}
                    >
                      <UserX size={18} />
                      Inactive
                    </button>
                  </div>
                  <p className="text-xs text-stone-500">
                    {editForm.is_active 
                      ? 'User can log in and participate in challenges'
                      : 'User will be blocked from logging in'
                    }
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-stone-700">Birthday</Label>
                  <Input
                    type="date"
                    value={editForm.birthdate}
                    onChange={(e) => setEditForm({ ...editForm, birthdate: e.target.value })}
                    className="bg-stone-50 border-stone-300 text-stone-900"
                    data-testid="edit-user-birthdate"
                  />
                  <p className="text-xs text-stone-500">
                    {editForm.birthdate 
                      ? `User will receive a birthday message on ${new Date(editForm.birthdate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`
                      : 'No birthday set - user won\'t receive birthday messages'
                    }
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-stone-700">Exclude from Leaderboard</Label>
                      <p className="text-xs text-stone-500 mt-1">
                        Hide this user from the public leaderboard rankings
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditForm({ ...editForm, exclude_from_leaderboard: !editForm.exclude_from_leaderboard })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        editForm.exclude_from_leaderboard ? 'bg-[#EFB342]' : 'bg-stone-200'
                      }`}
                      data-testid="exclude-leaderboard-toggle"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          editForm.exclude_from_leaderboard ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-stone-700">Show as Coach on Leaderboard</Label>
                      <p className="text-xs text-stone-500 mt-1">
                        Displays this user inline in their natural point position with a ★ Coach tag, but they do not consume a rank slot or count toward totals.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setEditForm({ ...editForm, show_as_coach_on_leaderboard: !editForm.show_as_coach_on_leaderboard })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        editForm.show_as_coach_on_leaderboard ? 'bg-[#EFB342]' : 'bg-stone-200'
                      }`}
                      data-testid="show-as-coach-toggle"
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          editForm.show_as_coach_on_leaderboard ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-stone-700">Subscription Start Date</Label>
                  <Input
                    type="date"
                    value={editForm.subscription_start_date}
                    onChange={(e) => setEditForm({ ...editForm, subscription_start_date: e.target.value })}
                    className="bg-stone-50 border-stone-300 text-stone-900"
                    data-testid="edit-user-subscription-start"
                  />
                  <p className="text-xs text-stone-500">
                    {editForm.subscription_start_date 
                      ? `Renewal reminder will be sent 14 days before ${new Date(editForm.subscription_start_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} each year`
                      : 'Set this to enable annual renewal reminders'
                    }
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setEditingUser(null)}
                  className="flex-1 border-stone-300 text-stone-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-[#EFB342] text-black hover:bg-[#D99F30]"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingUser && (
          <div className="fixed inset-0 bg-[#F5F4F1]/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white border border-stone-200 rounded-2xl p-6 w-full max-w-md animate-fade-in">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                  <Trash2 className="text-red-500" size={24} />
                </div>
                <div>
                  <h3 className="font-heading text-xl font-bold text-stone-900">Delete User</h3>
                  <p className="text-stone-500 text-sm">This action cannot be undone</p>
                </div>
              </div>
              
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 mb-6">
                <p className="text-stone-900 font-medium">{deletingUser.name}</p>
                <p className="text-stone-600 text-sm">{deletingUser.email}</p>
                <p className="text-stone-500 text-xs mt-2">
                  Total Points: {deletingUser.total_points || 0} • 
                  All check-ins and notifications will be deleted
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDeletingUser(null)}
                  className="flex-1 border-stone-300 text-stone-700"
                  disabled={deleting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex-1 bg-red-600 text-stone-900 hover:bg-red-700"
                >
                  {deleting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Deleting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Trash2 size={16} />
                      Delete User
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add User Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-[#F5F4F1]/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white border border-stone-200 rounded-2xl p-6 w-full max-w-md animate-fade-in">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#EFB342]/20 rounded-full flex items-center justify-center">
                    <UserPlus className="text-[#EFB342]" size={24} />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-stone-900">Add New User</h2>
                    <p className="text-stone-500 text-sm">User will receive a welcome email</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowAddModal(false);
                    setAddForm({ name: '', email: '', password: '' });
                  }}
                  className="text-stone-600 hover:text-stone-900"
                >
                  <X size={20} />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-stone-700">Full Name</Label>
                  <Input
                    value={addForm.name}
                    onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                    placeholder="Enter user's name"
                    className="bg-stone-50 border-stone-300 text-stone-900"
                    data-testid="add-user-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-stone-700">Email</Label>
                  <Input
                    type="email"
                    value={addForm.email}
                    onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                    placeholder="user@example.com"
                    className="bg-stone-50 border-stone-300 text-stone-900"
                    data-testid="add-user-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-stone-700">Temporary Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={addForm.password}
                      onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                      placeholder="Min 8 characters"
                      className="bg-stone-50 border-stone-300 text-stone-900 pr-10"
                      data-testid="add-user-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-700"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-xs text-stone-500">
                    User can change this password after logging in
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddModal(false);
                    setAddForm({ name: '', email: '', password: '' });
                  }}
                  className="flex-1 border-stone-300 text-stone-700"
                  disabled={adding}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddUser}
                  disabled={adding}
                  className="flex-1 bg-[#EFB342] text-black hover:bg-[#D99F30]"
                  data-testid="add-user-submit"
                >
                  {adding ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                      Creating...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <UserPlus size={16} />
                      Create User
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Badge Details Modal */}
        <Dialog open={!!badgeModalUser} onOpenChange={() => setBadgeModalUser(null)}>
          <DialogContent className="bg-white border-stone-200 text-stone-900 max-w-md max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Award className="text-[#EFB342]" size={24} />
                {badgeModalUser?.name}'s Badges
              </DialogTitle>
            </DialogHeader>
            
            <div className="mt-4">
              <p className="text-stone-600 text-sm mb-4">
                {badgeModalUser?.badges?.length || 0} total badges earned
              </p>
              
              {badgeModalUser?.badges?.length > 0 ? (
                <div className="space-y-3">
                  {[...(badgeModalUser?.badges || [])]
                    .sort((a, b) => (b.earned_at || '').localeCompare(a.earned_at || ''))
                    .map((badge, i) => {
                      const earnedDate = badge.earned_at 
                        ? new Date(badge.earned_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })
                        : 'Unknown';
                      
                      // Check if earned this month
                      const now = new Date();
                      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
                      const isThisMonth = (badge.earned_at || '') >= currentMonthStart;
                      
                      return (
                        <div 
                          key={i} 
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            isThisMonth ? 'bg-[#EFB342]/10 border border-[#EFB342]/30' : 'bg-stone-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#EFB342] hexagon flex items-center justify-center flex-shrink-0">
                              <Award className="text-black" size={20} />
                            </div>
                            <div>
                              <p className="text-stone-900 font-medium">{badge.badge_name}</p>
                              <p className="text-xs text-stone-500">Earned {earnedDate}</p>
                            </div>
                          </div>
                          {isThisMonth && (
                            <span className="px-2 py-1 bg-[#EFB342]/20 text-[#EFB342] text-xs rounded-full font-medium whitespace-nowrap">
                              This Month
                            </span>
                          )}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <div className="text-center py-8 text-stone-500">
                  <Award size={48} className="mx-auto mb-2 text-stone-400" />
                  <p>No badges earned yet</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminUsers;
